import React, { useCallback, useEffect, useMemo, useState } from "react"
import { CSSTransition } from "react-transition-group"
import i18nData from "@rogue_4/locales/zh-CN.json"
import { StyledComponent } from "@rogue_4/types"
import classNames from "classnames"
import Scroll from "@hg/scroll"

import { i18n } from "@/utils/i18n"
import CHARACTER_DICT from "../../../data/character"
import DIALOG_DICT, { DIALOG_ARR } from "../../../data/dialog"
import { useGameProgress } from "../../../store"
import { THistory } from "../../../type"
import { SFX } from "../../../utils/sound"
import { getTransitionClassnames } from "../../../utils/transition"
import ModalTitle from "../common/modalTitle"
import { useGameModalStore } from "../store"

import styles from "./index.module.less"

const HistoryModal: StyledComponent = ({ className, style }) => {
    const [active, setActive] = useState(false)
    const [history, setHistory] = useState<THistory[]>()
    useEffect(() => {
        useGameModalStore.setState({
            callHistoryModal: (history?: THistory[]) => {
                setActive(true)
                setHistory(history)
            },
        })
    }, [])
    const handleClose = useCallback(() => {
        SFX.play("commonClick")
        setActive(false)
    }, [])
    const { currentConversation, currentDialog, choice } = useGameProgress()
    const calcHistory = useMemo<THistory[]>(() => {
        if (history) return history
        if (!currentConversation) return []
        let dialog = DIALOG_ARR[0]
        let conv = dialog.dialog[dialog.start]

        const ret: THistory[] = []
        // fix first conv
        if (conv.key === currentConversation.key) {
            ret.push({
                character: conv.character,
                description: conv.description,
            })
            return ret
        }

        do {
            if (!conv.choice) {
                ret.push({
                    character: conv.character,
                    description: conv.description,
                })
                if (conv.next === "NEXT_DIALOG") {
                    dialog = DIALOG_DICT[dialog.next]
                    if (!dialog) break
                    conv = dialog.dialog[dialog.start]
                } else {
                    conv = dialog.dialog[conv.next]
                }
            } else {
                ret.push({
                    character: conv.character,
                    description: conv.description,
                })
                if (choice[conv.key]) {
                    const choiceSelected = conv.choice.findIndex(
                        (c) => c.key === choice[conv.key]
                    )
                    ret.push({
                        choice: conv.choice.map((c) => c.description),
                        active: choiceSelected,
                    })
                    if (choiceSelected === -1) break
                    conv = dialog.dialog[conv.choice[choiceSelected].next]
                } else {
                    break
                }
            }
        } while (conv.key !== currentConversation.key)
        ret.push({
            character: conv.character,
            description: conv.description,
        })

        return ret
    }, [currentConversation, choice, history])
    return (
        <CSSTransition
            in={active}
            timeout={300}
            classNames={getTransitionClassnames(styles)}
        >
            <div
                className={classNames(styles.historyModal, className)}
                style={style}
                onClick={(e) => {
                    e.stopPropagation()
                }}
            >
                <ModalTitle className={styles.title} brain>
                    {i18n(i18nData, "rogue_4.game.title.history")}
                </ModalTitle>
                <Scroll
                    direction="y"
                    className={styles.scrollContainer}
                    scrollBarClassName={styles.scrollBar}
                    thumbClassName={styles.thumb}
                    autoHideScrollBar={false}
                    scrollBar
                    rotateDetection
                >
                    <div className={styles.contentContainer}>
                        {calcHistory.map((h, index) =>
                            h["character"] ? (
                                <div
                                    className={styles.conversation}
                                    key={index}
                                >
                                    {h["character"] !== "BATTLE" && (
                                        <div className={styles.character}>
                                            {
                                                CHARACTER_DICT[h["character"]]
                                                    ?.name
                                            }
                                        </div>
                                    )}
                                    <div
                                        className={classNames(
                                            styles.content,
                                            h["character"] === "BATTLE" &&
                                                styles.battle
                                        )}
                                    >
                                        {(h["description"] as string)
                                            .split("\n")
                                            .map((s) => (
                                                <div
                                                    className={styles.line}
                                                    key={s}
                                                    dangerouslySetInnerHTML={{
                                                        __html: s,
                                                    }}
                                                ></div>
                                            ))}
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={classNames(
                                        styles.conversation,
                                        styles.choice
                                    )}
                                    key={index}
                                >
                                    <div className={styles.character}>
                                        {i18n(
                                            i18nData,
                                            "rogue_4.game.history.choose"
                                        )}
                                        &gt;
                                    </div>
                                    <div className={styles.content}>
                                        {(h["choice"] as string[]).map(
                                            (s, index) => (
                                                <div
                                                    key={index}
                                                    className={classNames(
                                                        styles.line,
                                                        index !== h["active"] &&
                                                            styles.disabled
                                                    )}
                                                >
                                                    {s}
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </Scroll>
                <div className={styles.backButton} onClick={handleClose}></div>
            </div>
        </CSSTransition>
    )
}

export default HistoryModal
